
import { Component, mixins, Watch } from 'nuxt-property-decorator';
import {
  format as dateFormat,
  isAfter,
  differenceInDays,
  startOfDay,
} from 'date-fns';
import { VNodeChildren } from 'vue';
import HotelViewRoot from '../../-index';
import {
  HFullModal,
  HNotification,
  HPeoplesForm,
  HVacancyCalendar,
  HVacancyCalendarRef,
  HSelectItem,
  IconName,
  HBtn,
  HSelect,
  HPathIcon,
  HPrice,
  HDynamicAirPackage,
  HDynamicAirPackageSettings,
} from '~/components';
import { VacancySearchConditionsMixin } from '~/mixins/vacancy-search-conditions';
import {
  YgetsableHotelDetail,
  VacancySearchConditions,
  createDefaultVacancySearchConditions,
  isYgetsableHotelDetail,
  NightRange,
} from '~/schemes';
import {
  toDate,
  withTourListCampaign,
  TourListLinkOptions,
  TOUR_LIST_CAMPAIGNS,
  TOUR_LIST_LINK_OPTIONS,
} from '~/helpers';

/**
 * 全ての決済方法アイコン画像のパス
 */
const allPaymentMethodIconPaths = [
  '/common/payment-methods/eikyufumetsu-points.svg',
  '/common/payment-methods/paidy.svg',
  '/common/payment-methods/jcb.svg',
  '/common/payment-methods/diners.svg',
  '/common/payment-methods/american-express.png',
  '/common/payment-methods/mastercard.svg',
  '/common/payment-methods/visa.svg',
  '/common/payment-methods/hoshino-resort-gift-card.svg',
];

/**
 * 特定の施設の決済方法アイコン画像のパス
 * @see https://hr-dev.backlog.jp/view/ACCO_CMS-2129
 */
const paymentMethodIconsByHotelId: Record<string, string[]> = {
  // 星のやバリ: VISA, MasterCard, AMERICAN EXPRESS, JCB
  '62ea1899a32202e0e15ea0ba': [
    '/common/payment-methods/jcb.svg',
    '/common/payment-methods/american-express.png',
    '/common/payment-methods/mastercard.svg',
    '/common/payment-methods/visa.svg',
  ],
  // 星のやグーグァン: VISA, MasterCard, JCB
  '62ea1a6ba32202e0e15ea0bc': [
    '/common/payment-methods/jcb.svg',
    '/common/payment-methods/mastercard.svg',
    '/common/payment-methods/visa.svg',
  ],
  // リゾナーレグアム: Visa, Mastercard, American Express, Diners Club, JCB
  '631953226bbba3aa3508921d': [
    '/common/payment-methods/jcb.svg',
    '/common/payment-methods/diners.svg',
    '/common/payment-methods/american-express.png',
    '/common/payment-methods/mastercard.svg',
    '/common/payment-methods/visa.svg',
  ],
  // リゾナーレ大阪: Visa, Mastercard, American Express, Diners Club, JCB
  '6311bb0ac71cb52cc60276aa': [
    '/common/payment-methods/jcb.svg',
    '/common/payment-methods/diners.svg',
    '/common/payment-methods/american-express.png',
    '/common/payment-methods/mastercard.svg',
    '/common/payment-methods/visa.svg',
  ],
  // サーフジャックハワイ: Visa, Mastercard, American Express, JCB
  '62eb56a7a32202e0e15ea0c2': [
    '/common/payment-methods/jcb.svg',
    '/common/payment-methods/american-express.png',
    '/common/payment-methods/mastercard.svg',
    '/common/payment-methods/visa.svg',
  ],
};

@Component<MyView & VacancySearchConditionsMixin>({
  inject: ['hotelViewRoot'],
  head() {
    return {
      title: this.title,
    };
  },
  async asyncData(ctx) {
    const { $hotel, $api, params, $error, store } = ctx;
    try {
      // 施設データを宿GETSマスタとセットでロードする
      // 重複リクエストは1つにまとめられるので、ルートビューの読み込みとバッティングすることはない
      // あと、通過設定も必要なので、このページではSSR側でもロードしておく
      await Promise.all([
        $api.hotels.getBySlug(params.hotel_slug).then(async (hotelSource) => {
          if (!hotelSource) {
            throw $error.create(404);
          }

          await $hotel.setCurrent(hotelSource);

          // 宿GETSマスタが必須なので、このページではasyncData内で取得しておく
          await $hotel.loadCurrentHotelYgetsDetail();
        }),
        store.dispatch('commons/clientInit', ctx),
      ]);
    } catch (err) {
      $error.throw(err);
    }
  },
  render() {
    const {
      hotel,
      dynamicPackage,
      dynamicAirPackageSettings,
      notifications,
      canBook,
    } = this;

    // サイトの現在の表示言語
    const currntLanguage = this.$language.current;
    // Paidyの分割払いの回数
    const paymentCount = 6;
    // Paidy用の「1泊1名を6で割った金額」
    let paidyPrice: number | undefined;
    /** Paidyが利用可能な施設か、かつ現在の表示言語が日本語であるかどうか */
    const canUsePaidy = hotel.ygets.paidyEnableFlg && currntLanguage === 'ja';

    if (canUsePaidy && this.totalLowestPrice) {
      paidyPrice = Math.ceil(this.totalLowestPrice / paymentCount);
    }

    return (
      <div staticClass="my-hotel-roomsearch-view">
        <HFullModal
          v-model={this.modalActive}
          onAfterLeave={this.onModalAfterLeave}
          scopedSlots={{
            header: () => (
              <h1 staticClass="my-title">
                <span staticClass="my-title__hotel-name">{hotel.name}</span>
                <span staticClass="my-title__suffix">{this.shortTitle}</span>
              </h1>
            ),
            footer: () => (
              <div staticClass="my-footer">
                <div staticClass="my-footer__info">
                  {canBook && (
                    <div staticClass="my-footer__price-info">
                      <p staticClass="my-footer__price-info__date">
                        {this.displayRange}
                      </p>
                      <p staticClass="my-footer__price-info__conditions">
                        <span staticClass="my-footer__price-info__peoples">
                          {this.conditionWords.map((word, index) => (
                            <span key={index}>{word}</span>
                          ))}
                        </span>
                        <span staticClass="my-footer__price-info__nights">
                          ({this.$tc('value.nights', this.stayLength)}){' '}
                        </span>
                        <span staticClass="my-footer__price-info__oneperson">
                          {this.$tc('label.guestSuffix', 1)}
                        </span>
                      </p>
                      {this.totalLowestPrice !== null && (
                        <div class="my-footer__price-info-area">
                          <HPrice
                            staticClass="my-footer__price-info__amount"
                            tag="p"
                            base={this.baseRate}>
                            {this.totalLowestPrice}
                            {this.$t('chore.dash')}
                          </HPrice>
                          {paidyPrice && (
                            <div staticClass="my-footer__price-info__paidy">
                              {paymentCount}
                              <span staticClass="my-footer__price-info__unit">
                                回
                              </span>
                              あと払いで
                              {paidyPrice}
                              <span staticClass="my-footer__price-info__unit">
                                {this.$t('chore.pricePerMonth')}
                              </span>
                              {this.$t('chore.dash')}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  <HBtn
                    v-ev={{
                      category: 'link',
                      action: this.bookUrl,
                      id: 'gfcalendar_button',
                      params: this.eventParams,
                    }}
                    staticClass="my-footer__info__btn-reserve"
                    color="primary"
                    stack={this.$t('guide.bestRateShort') as string}
                    depressed
                    href={this.bookUrl as string}
                    target="_blank"
                    rel="noopener"
                    disabled={!this.canBook}>
                    <span staticClass="my-footer__info__btn-reserve__label">
                      {this.$t('action.gotoBookARoom')}
                    </span>
                  </HBtn>
                  {!!dynamicAirPackageSettings && (
                    <HDynamicAirPackage
                      staticClass="my-footer__info__air"
                      settings={dynamicAirPackageSettings}
                      tourList={TOUR_LIST_LINK_OPTIONS.roomsearchFooter}
                    />
                  )}
                </div>
              </div>
            ),
          }}>
          {!!dynamicPackage && (
            <div
              staticClass="my-dynamic-package"
              class={{
                'my-dynamic-package--liquid': dynamicPackage.liquid,
              }}>
              <div staticClass="my-dynamic-package__inner h-container">
                {dynamicPackage.items.map((item) => (
                  <HBtn
                    key={item.key}
                    href={item.url}
                    target="_blank"
                    // GA計測のため明示的にリファラを設定
                    referrerpolicy="no-referrer-when-downgrade"
                    tourList={item.tourList}
                    staticClass="my-dynamic-package__link"
                    size="sm"
                    breadcrumb
                    bigIcon={item.icon}
                    bigIconLiquid={dynamicPackage.liquid}>
                    {item.text}
                  </HBtn>
                ))}
              </div>
            </div>
          )}

          <div staticClass="my-body h-container">
            <div staticClass="my-top">
              <p staticClass="my-bestrate-guide">
                <span staticClass="my-bestrate-guide__text">
                  {this.$t('guide.bestRate')}
                </span>
                {/* <HIcon staticClass="my-bestrate-guide__icon" name="question-outline" /> */}
              </p>

              {notifications.length > 0 && (
                <div staticClass="my-notifications">
                  {notifications.map((notification, index) => (
                    <HNotification
                      key={index}
                      staticClass="my-notifications__item"
                      dense
                      data={notification}
                    />
                  ))}
                </div>
              )}
            </div>

            <div staticClass="my-form">
              <div staticClass="my-form__body">
                <HPeoplesForm v-model={this.conditions} hotel={hotel} />
                <HSelect
                  v-model={this.stayLength}
                  staticClass="my-form__nights"
                  items={this.stayOptions}
                  size="lg"
                  backdrop
                  hideDetails
                />
              </div>
            </div>

            <HVacancyCalendar
              ref="vacancyCalendar"
              v-model={this.pickedDates}
              staticClass="my-vacancy-calendar"
              pullNarrow
              hotel={hotel}
              conditions={this.vacancySearchConditions}
              month={this.minMonth}
              min={this.minMonth}
            />

            <div staticClass="my-calendar-footer">
              <p staticClass="my-calendar-footer__notice">
                {this.$t('guide.isLowestRatePerPersonOnFirstNight')}
                {/** @TODO 宿GETS設定によってGTTが有効になっている場合のみ表示される */}
                {/* this.gttEnabled && [
                  <br />,
                  'GoToキャンペーン適用の場合、上記表示金額より35%offとなります。',
                ] */}
              </p>
              <div staticClass="my-calendar-footer__advance">
                <ul staticClass="my-calendar-footer__guides">
                  {/* 空室 */}
                  <li staticClass="my-calendar-footer__guide">
                    <HPathIcon
                      staticClass="my-calendar-footer__guide__icon"
                      name="calendar-green"
                    />
                    <span staticClass="my-calendar-footer__guide__label">
                      {this.$t('vacancyCalendar.available')}
                    </span>
                  </li>
                  {/* 残りわずか */}
                  <li staticClass="my-calendar-footer__guide">
                    <HPathIcon
                      staticClass="my-calendar-footer__guide__icon"
                      name="calendar-warn"
                    />
                    <span
                      staticClass="my-calendar-footer__guide__label"
                      domPropsInnerHTML={
                        this.$t('vacancyCalendar.fewLeft') as string
                      }
                    />
                  </li>
                  {/* チェックイン不可 */}
                  <li staticClass="my-calendar-footer__guide">
                    <HPathIcon
                      staticClass="my-calendar-footer__guide__icon"
                      name="calendar-empty"
                    />
                    <span
                      staticClass="my-calendar-footer__guide__label"
                      domPropsInnerHTML={
                        this.$t('vacancyCalendar.cannotArrive') as string
                      }
                    />
                  </li>
                  {/* 休館 */}
                  <li staticClass="my-calendar-footer__guide">
                    <HPathIcon
                      staticClass="my-calendar-footer__guide__icon"
                      name="calendar-closed"
                    />
                    <span staticClass="my-calendar-footer__guide__label">
                      {this.$t('vacancyCalendar.closed')}
                    </span>
                  </li>
                </ul>
                <ul staticClass="my-calendar-footer__payment-methods">
                  {this.paymentMethodIconPaths.map((iconPath) => (
                    <li>
                      <img
                        staticClass="my-calendar-footer__payment-methods__icon"
                        src={this.$res.img(`${iconPath}`)}
                        alt={iconPath}
                      />
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </HFullModal>
      </div>
    );
  },
})
export default class MyView extends mixins<VacancySearchConditionsMixin>(
  VacancySearchConditionsMixin,
) {
  readonly hotelViewRoot!: HotelViewRoot;

  $refs!: {
    vacancyCalendar: HVacancyCalendarRef;
  };

  conditions: VacancySearchConditions = createDefaultVacancySearchConditions(
    this.hotel,
  );

  modalActive: boolean = true;
  pickedDates: string[] = [];
  /**
   * hotelNightDefaultを使用 空室検索のデフォルトの泊数を宿GETSで設定した初期値にしたいため
   */
  stayLength: number = this.hotelNightDefault;

  /**
   * 施設詳細情報
   *
   * * グローバルストアを信頼しているので、ちょっとここは危ない実装
   */
  get hotel(): YgetsableHotelDetail {
    const { current } = this.$hotel;
    if (!current) {
      throw new Error('missing hotel');
    }

    if (!isYgetsableHotelDetail(current)) {
      throw new Error('missing ygets information.');
    }
    return current;
  }

  get baseRate() {
    return this.ygets.baseRate;
  }

  get ygets() {
    const { ygets } = this.hotel;
    if (!ygets) throw new Error('missing ygets info');
    return ygets;
  }

  /**
   * GTT有効施設であるか
   */
  get gttEnabled() {
    return this.ygets.gttEnableFlg;
  }

  /**
   * 基本はすべての決済方法アイコンを表示するが、
   * 施設によっては利用できる決済方法が制限されている場合があるため、
   * その場合は制限された決済方法アイコンのみを返す
   */
  get paymentMethodIconPaths(): string[] {
    const hotelId = this.hotel.id;

    if (hotelId && paymentMethodIconsByHotelId[hotelId]) {
      return paymentMethodIconsByHotelId[hotelId];
    }

    // 決済方法に制限がないので全ての決済方法アイコンを表示
    return allPaymentMethodIconPaths;
  }

  get minMonth(): string | undefined {
    const { searchDateDefault } = this.ygets;
    if (searchDateDefault) {
      const _default = toDate(searchDateDefault);
      if (isAfter(_default, new Date())) {
        return searchDateDefault;
      }
    }
  }

  /** 泊数の下限を取得 */
  get hotelNightMin(): number {
    return this.ygets.hotelNightMin;
  }

  /** 最大宿泊可能数を取得 */
  get maxStayLength(): number {
    return this.ygets.maxStayLength || 8;
  }

  /** 泊数初期値を取得 */
  get hotelNightDefault(): number {
    return this.ygets.hotelNightDefault || 1;
  }

  get stayOptions(): HSelectItem[] {
    const options: HSelectItem[] = [];
    const { nightsMemos } = this.hotel;
    const { hotelNightMin, maxStayLength } = this;
    for (let value = hotelNightMin, l = maxStayLength + 1; value < l; value++) {
      const infos = nightsMemos.filter((m) => {
        const { from, to } = m.nights as NightRange;
        if (from === to) {
          return from === value;
        } else {
          return from && to && value >= from && value <= to;
        }
      });

      const labelChildren: VNodeChildren = [
        this.$tc('value.nights', value) as string,
      ];
      // 宿泊数が重複した場合は初めに設定された注記を表示
      if (infos.length > 0) {
        labelChildren.push(<small>{` (${infos[0].memo})`}</small>);
      }
      options.push({
        label: () => {
          return <span>{labelChildren}</span>;
        },
        value,
      });
    }
    return options;
  }

  get title() {
    return this.$t('name.vacancySearch') as string;
  }

  get shortTitle() {
    return this.$t('name.vacancySearchShort') as string;
  }

  get maxReservationNumber() {
    return this.ygets.maxReservationNumber;
  }

  get useSearch() {
    return this.ygets.useSearch;
  }

  get vacancySearchConditions(): VacancySearchConditions {
    return {
      ...this.conditions,
      stayLength: this.stayLength,
    };
  }

  get dynamicAirPackageSettings():
    | HDynamicAirPackageSettings
    | string
    | undefined {
    return this.airUrl || undefined; // パラメータ連携したい場合は↓を使う
    // const { ygets, vacancySearchConditions, airUrl, checkIn } = this;
    // const { tbConnectionSettings } = ygets;
    // if (
    //   !checkIn ||
    //   !tbConnectionSettings ||
    //   !tbConnectionSettings.connectionFlag
    // ) {
    //   return airUrl || undefined;
    // }
    // return {
    //   checkIn,
    //   ...vacancySearchConditions,
    //   tb: tbConnectionSettings,
    // };
  }

  get fromDt(): Date | null {
    const from = this.pickedDates[0];
    return from ? toDate(from) : null;
  }

  get toDt(): Date | null {
    const to = this.pickedDates[1];
    return to ? toDate(to) : null;
  }

  get displayRange(): string {
    let str: string = '';

    const {
      fromDt,
      // toDt,
    } = this;
    if (fromDt) {
      // const year = fromDt.getFullYear();
      // const month = fromDt.getMonth() + 1;
      // const day = fromDt.getDate();
      // str += this.$t('value.ymd', {
      //   year,
      //   month,
      //   day,
      // });
      str += this.$d(fromDt, 'short', this.$language.info.attr);

      /**
       * @MEMO
       * 範囲選択はなくなったけど将来的にはやりたいかもとのこと
       */
      // if (toDt) {
      //   const day = toDt.getDate();
      //   str +=
      //     ' - ' +
      //     this.$t('value.day', {
      //       day,
      //     });
      //   str += ` ( ${this.$tc('value.nights', this.stayLength)} )`;
      // }
    }
    return str;
  }

  get notifications() {
    return this.$hotel.allNotifications;
  }

  get airUrl() {
    return this.hotel.dynamicPackage.air;
  }

  get shinkansenUrl() {
    return this.hotel.dynamicPackage.shinkansen;
  }

  get picked() {
    return this.pickedDates.length === 1;
  }

  get canBook() {
    return this.picked;
  }

  get checkIn() {
    const { fromDt } = this;
    return (fromDt && dateFormat(fromDt, 'yyyy/MM/dd')) || undefined;
  }

  get bookUrl(): string | void {
    if (!this.canBook) return;
    const { vacancySearchConditions: conditions, stayLength, checkIn } = this;
    if (!checkIn) return;

    const url = this.$hotel.createYgetsBookUrl(this.hotel, {
      checkIn,
      stayLength,
      ...(conditions as any),
    });
    return url;
  }

  get eventParams(): string | void {
    const { vacancySearchConditions: conditions, stayLength, fromDt } = this;
    if (!fromDt) return;
    const { adult, underTwelve, underSeven, underFour } = conditions;
    const differenceFromToday = differenceInDays(
      fromDt,
      startOfDay(Date.now()),
    );
    return [
      differenceFromToday,
      stayLength,
      adult,
      underTwelve,
      underSeven,
      underFour,
    ].join(',');
  }

  onModalAfterLeave() {
    let { beforeRoute } = this.$navigation;
    if (beforeRoute && !beforeRoute.name) beforeRoute = null;
    this.$navigation.backOrGo(
      (beforeRoute as any) || this.$hotel.location('/', this.hotel),
    );
  }

  pickedLowestPrice: number | null = null;

  get totalLowestPrice(): number | null {
    const { pickedLowestPrice } = this;
    if (pickedLowestPrice === null) {
      return null;
    } else {
      return pickedLowestPrice;
    }
  }

  get pickedDate(): string | null {
    return this.pickedDates[0] || null;
  }

  get dynamicPackage() {
    const { air, shinkansen, rentalCar } = this.hotel.dynamicPackage;
    const items: {
      key: string;
      text: string;
      url: string;
      icon: IconName;
      tourList: TourListLinkOptions;
    }[] = [];
    const { roomsearchBanner } = TOUR_LIST_LINK_OPTIONS;
    if (air) {
      items.push({
        key: 'air',
        text: this.$t('label.dynamicPacage.air') as string,
        url: air,
        icon: 'airplane-outline',
        tourList: withTourListCampaign(
          roomsearchBanner,
          TOUR_LIST_CAMPAIGNS.air,
        ),
      });
    }
    if (shinkansen) {
      items.push({
        key: 'shinkansen',
        text: this.$t('label.dynamicPacage.shinkansen') as string,
        url: shinkansen,
        icon: 'shinkansen-outline',
        tourList: withTourListCampaign(
          roomsearchBanner,
          TOUR_LIST_CAMPAIGNS.shinkansen,
        ),
      });
    }
    if (rentalCar) {
      items.push({
        key: 'rentalCar',
        text: this.$t('label.dynamicPacage.rentalCar') as string,
        url: rentalCar,
        icon: 'car-outline',
        tourList: withTourListCampaign(
          roomsearchBanner,
          TOUR_LIST_CAMPAIGNS.rentalCar,
        ),
      });
    }
    const { length } = items;
    if (length > 0) {
      return {
        liquid: length > 1,
        items,
      };
    }
  }

  /** 選択した日付を監視して指定宿泊数の最低金額を算出する */
  @Watch('pickedDate')
  protected pickedDateChangeHandler() {
    let pickedLowestPrice: number | null = null;
    const { vacancyCalendar } = this.$refs;
    if (vacancyCalendar) {
      pickedLowestPrice = vacancyCalendar.totalLowestPrice;
    }
    this.pickedLowestPrice = pickedLowestPrice;
  }

  @Watch('vacancySearchConditions', { deep: true })
  protected conditionChangeHandler() {
    this.reset();
  }

  @Watch('stayLength')
  protected stayLengthChangeHandler() {
    this.reset();
  }

  reset() {
    this.pickedDates = [];
    this.pickedLowestPrice = null;
  }
}
